import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react'
import {
  buildCatalogFromDashboardDefinition,
  hasWidgetHydrationPayload,
  normalizeDashboardDefinition,
  validateDashboard,
} from '../../builderCore'
import { parseDashboardDefinitionJson } from '../../coerceDashboardDefinition'
import type { DashboardDataSource } from '../../dataSource'
import { dateRangeDependsOnCatalogPresets, getEffectiveSlicerPanel } from '../../runtime'
import { sanitizeDashboardSection } from '../../sections'
import type {
  AgentBuilderDashboard,
  DashboardDefinition,
  DashboardRuntimeDateRange,
  DashboardWidget,
  DatePresetOption,
  SavedDashboardViewState,
} from '../../types'
import type { DrillOrder } from '../../drilldownHierarchy'
import { reconcileViewState, serializeViewState, type ReconcileViewResult } from '../savedViews'
import { dashboardViewReducer } from './reducer'
import { createWidgetRequestIntent } from './intent'
import { createDashboardRequestCoordinator } from './requestCoordinator'
import {
  selectAppliedFilters,
  selectDraftFilters,
  selectHasPendingGlobalFilterChanges,
  selectHydratableWidgetIds,
  selectInsightInvalidationSignature,
  selectSlicerLoadingByField,
  selectSlicerOptionsByField,
} from './selectors'
import type {
  DashboardViewAction,
  WidgetDrilldownState,
} from './state'
import { createDashboardViewState } from './state'

/** What the engine needs from a saved-dashboard query; the app passes React Query's result. */
export type SavedDashboardResult = {
  data: AgentBuilderDashboard | null | undefined
  isLoading: boolean
  isError: boolean
  isSuccess: boolean
}

const NO_SAVED_DASHBOARD: SavedDashboardResult = { data: undefined, isLoading: false, isError: false, isSuccess: false }

function useNoSavedDashboard(): SavedDashboardResult {
  return NO_SAVED_DASHBOARD
}

/** The catalog's date presets; `isLoading` holds the first load until they settle. */
export type DatePresetOptionsResult = {
  data: DatePresetOption[] | undefined
  isLoading: boolean
}

const NO_DATE_PRESET_OPTIONS: DatePresetOptionsResult = { data: undefined, isLoading: false }

function useNoDatePresetOptions(): DatePresetOptionsResult {
  return NO_DATE_PRESET_OPTIONS
}

export type UseDashboardViewArgs = {
  clientId: string
  dataSource: DashboardDataSource
  /**
   * Loads the saved copy of `initialDashboardId`. A hook, called on every render,
   * so it must stay the same function. Leave out when the dashboard is given in full.
   */
  useSavedDashboard?: (dashboardId: string | null, clientId: string) => SavedDashboardResult
  /** Like `useSavedDashboard`, a hook that must stay the same function. */
  useDatePresetOptions?: (clientId: string, platformTypeId: number) => DatePresetOptionsResult
  initialDashboard: DashboardDefinition | null
  initialDashboardId: string | null
  onPersistDashboard?: (dashboard: DashboardDefinition, dashboardId: string | null) => void
  onWarning?: (message: string) => void
  getWidgetDrillOrder?: (widget: DashboardWidget) => Pick<DrillOrder, 'mode' | 'dimensions'> | undefined
  /**
   * The saved view to open with (the user's default, or a ?view= link). While `pending`, the first
   * load waits so widgets are queried once, with the view already applied. Omit when saved views are off.
   */
  initialView?: { pending: boolean; view: SavedDashboardViewState | null }
  onInitialViewApplied?: (result: ReconcileViewResult) => void
}

function parseSavedDashboardDefinition(savedDashboard: AgentBuilderDashboard | null): DashboardDefinition | null {
  const parsed = parseDashboardDefinitionJson(savedDashboard?.dashboardJson)
  if (!parsed) return null
  const platformTypeId = savedDashboard?.platformTypeId || parsed.platformTypeId
  return {
    ...parsed,
    platformTypeId,
    section: sanitizeDashboardSection(savedDashboard?.section ?? parsed.section, platformTypeId),
  }
}

function getDashboardStructureKey(dashboard: DashboardDefinition | null, dashboardId: string | null) {
  return JSON.stringify({
    dashboardId,
    section: dashboard?.section ?? null,
    title: dashboard?.title ?? '',
    widgetIds: dashboard?.widgets.map((widget) => widget.id) ?? [],
    slicerPanel: dashboard?.slicerPanel ?? null,
    dashboardFilters: dashboard?.dashboardFilters ?? [],
  })
}

function getWidgetRefreshSignature(widget: DashboardWidget) {
  return JSON.stringify({
    id: widget.id,
    type: widget.type,
    dimensions: widget.dimensions,
    measures: widget.measures,
    filters: widget.filters,
    rowDimension: widget.rowDimension,
    trendGranularity: widget.trendGranularity,
    comparePreviousYear: widget.comparePreviousYear,
    daxGenerationRequest: widget.daxGenerationRequest,
    availableTypes: widget.availableTypes,
  })
}

function getChangedWidgetIds(
  currentDashboard: DashboardDefinition | null,
  nextDashboard: DashboardDefinition,
  fallbackClientId: string,
) {
  if (!currentDashboard) {
    return nextDashboard.widgets.filter(hasWidgetHydrationPayload).map((widget) => widget.id)
  }

  const globalDataScopeChanged = JSON.stringify({
    clientId: currentDashboard.clientId ?? fallbackClientId,
    platformTypeId: currentDashboard.platformTypeId,
    dashboardFilters: currentDashboard.dashboardFilters ?? [],
    slicerPanel: currentDashboard.slicerPanel ?? null,
  }) !== JSON.stringify({
    clientId: nextDashboard.clientId ?? fallbackClientId,
    platformTypeId: nextDashboard.platformTypeId,
    dashboardFilters: nextDashboard.dashboardFilters ?? [],
    slicerPanel: nextDashboard.slicerPanel ?? null,
  })

  if (globalDataScopeChanged) {
    return nextDashboard.widgets.filter(hasWidgetHydrationPayload).map((widget) => widget.id)
  }

  const currentSignatures = new Map(
    currentDashboard.widgets.map((widget) => [widget.id, getWidgetRefreshSignature(widget)]),
  )

  return nextDashboard.widgets
    .filter(hasWidgetHydrationPayload)
    .filter((widget) => currentSignatures.get(widget.id) !== getWidgetRefreshSignature(widget))
    .map((widget) => widget.id)
}

export function useDashboardView({
  clientId,
  dataSource,
  useSavedDashboard = useNoSavedDashboard,
  useDatePresetOptions = useNoDatePresetOptions,
  initialDashboard,
  initialDashboardId,
  onPersistDashboard,
  onWarning,
  getWidgetDrillOrder,
  initialView,
  onInitialViewApplied,
}: UseDashboardViewArgs) {
  const [state, rawDispatch] = useReducer(
    dashboardViewReducer,
    createDashboardViewState(initialDashboard, initialDashboardId),
  )
  const stateRef = useRef(state)
  const getWidgetDrillOrderRef = useRef(getWidgetDrillOrder)
  getWidgetDrillOrderRef.current = getWidgetDrillOrder
  const sortTimeoutsRef = useRef<Record<string, ReturnType<typeof setTimeout> | null>>({})
  const lastBootstrapLoadKeyRef = useRef<string | null>(null)

  const savedDashboardQuery = useSavedDashboard(state.dashboardId, clientId)
  const shouldWaitForSavedDashboardBootstrap = Boolean(
    clientId
    && state.dashboardId
    && !savedDashboardQuery.data
    && savedDashboardQuery.isLoading,
  )

  useEffect(() => {
    stateRef.current = state
  }, [state])

  useEffect(() => () => {
    Object.values(sortTimeoutsRef.current).forEach((timer) => {
      if (timer) clearTimeout(timer)
    })
  }, [])

  const dispatch = useCallback((action: DashboardViewAction) => {
    const nextState = dashboardViewReducer(stateRef.current, action)
    stateRef.current = nextState
    rawDispatch(action)
    return nextState
  }, [])

  // A stored preset is kept only if the catalog lists it, and an older dashboard (no stored date
  // mode) takes the catalog's default preset. Hold the first load until the catalog settles and
  // that is applied, so widgets are not first queried with a period that then changes. A failed
  // catalog counts as an empty one: both fall back to the custom range. The query is shared with the page.
  const catalogPlatformTypeId = state.dashboard?.platformTypeId ?? 0
  const catalogQuery = useDatePresetOptions(clientId, catalogPlatformTypeId)
  const [catalogDefaultSettledFor, setCatalogDefaultSettledFor] = useState<DashboardDefinition | null>(null)
  const awaitingCatalogDefault = Boolean(
    clientId
    && catalogPlatformTypeId
    && state.dashboard
    && !state.dashboardLoad.initialLoadCompleted
    && catalogDefaultSettledFor !== state.dashboard
    && dateRangeDependsOnCatalogPresets(getEffectiveSlicerPanel(state.dashboard)?.dateRange),
  )
  // isLoading (not isPending) so a paused offline query does not hold the load, as on the builder page.
  const catalogSettled = !catalogQuery.isLoading
  // undefined until the catalog settles; [] once it has failed.
  const loadedDatePresetOptions = catalogQuery.data
  const catalogDatePresetOptions = useMemo(
    () => (catalogSettled ? loadedDatePresetOptions ?? [] : undefined),
    [catalogSettled, loadedDatePresetOptions],
  )
  const catalogDatePresetOptionsRef = useRef(catalogDatePresetOptions)
  catalogDatePresetOptionsRef.current = catalogDatePresetOptions
  useEffect(() => {
    if (!awaitingCatalogDefault || !catalogDatePresetOptions) return
    dispatch({ type: 'DATE_DEFAULT_RESOLVED', datePresetOptions: catalogDatePresetOptions })
    setCatalogDefaultSettledFor(stateRef.current.dashboard)
  }, [awaitingCatalogDefault, catalogDatePresetOptions, dispatch])

  // The saved view is checked against the client's date presets, so it waits for the catalog too.
  const onInitialViewAppliedRef = useRef(onInitialViewApplied)
  onInitialViewAppliedRef.current = onInitialViewApplied
  const [initialViewSettledFor, setInitialViewSettledFor] = useState<DashboardDefinition | null>(null)
  const awaitingInitialView = Boolean(
    initialView
    && state.dashboard
    && !state.dashboardLoad.initialLoadCompleted
    && initialViewSettledFor !== state.dashboard,
  )
  const initialViewPending = initialView?.pending ?? false
  const initialViewState = initialView?.view ?? null
  useEffect(() => {
    if (!awaitingInitialView || awaitingCatalogDefault || initialViewPending) return
    const dashboard = stateRef.current.dashboard
    if (!dashboard) return

    if (initialViewState) {
      if (!catalogDatePresetOptions) return
      const result = reconcileViewState(initialViewState, dashboard, catalogDatePresetOptions)
      if (!result.unsupported) {
        dispatch({ type: 'VIEW_APPLIED', ...result.state })
      }
      onInitialViewAppliedRef.current?.(result)
    }
    setInitialViewSettledFor(dashboard)
  }, [awaitingCatalogDefault, awaitingInitialView, catalogDatePresetOptions, dispatch, initialViewPending, initialViewState])

  const coordinator = useMemo(() => createDashboardRequestCoordinator({
    clientId,
    dataSource,
    dispatch,
    getState: () => stateRef.current,
    getWidgetDrillOrder: (widget) => getWidgetDrillOrderRef.current?.(widget),
  }), [clientId, dataSource, dispatch])

  const dashboardStructureKey = useMemo(
    () => getDashboardStructureKey(state.dashboard, state.dashboardId),
    [state.dashboard, state.dashboardId],
  )

  useEffect(() => {
    if (!savedDashboardQuery.data) return

    const savedDefinition = parseSavedDashboardDefinition(savedDashboardQuery.data)
    if (!savedDefinition) {
      onWarning?.('Saved dashboard JSON could not be loaded from the server copy.')
      return
    }

    const savedCatalog = buildCatalogFromDashboardDefinition(savedDefinition)
    const normalized = normalizeDashboardDefinition(savedDefinition, savedCatalog)
    const savedPlatformTypeId = savedDashboardQuery.data.platformTypeId || normalized.platformTypeId
    const normalizedWithSection = {
      ...normalized,
      platformTypeId: savedPlatformTypeId,
      section: sanitizeDashboardSection(savedDashboardQuery.data.section ?? normalized.section, savedPlatformTypeId),
    }
    const validation = validateDashboard(normalizedWithSection, savedCatalog)
    if (!validation.ok) {
      onWarning?.(`Saved dashboard is invalid: ${validation.errors.join('; ')}`)
      return
    }

    const resolvedDashboardId = savedDashboardQuery.data.dashboardId ?? stateRef.current.dashboardId
    const currentSignature = stateRef.current.dashboard ? JSON.stringify(stateRef.current.dashboard) : null
    const nextSignature = JSON.stringify(normalizedWithSection)
    if (currentSignature === nextSignature && resolvedDashboardId === stateRef.current.dashboardId) {
      return
    }
    if (currentSignature === nextSignature) {
      dispatch({
        type: 'SET_DASHBOARD_ID',
        dashboardId: resolvedDashboardId,
      })
      return
    }

    dispatch({
      type: 'BOOTSTRAP_SUCCEEDED',
      dashboard: normalizedWithSection,
      dashboardId: resolvedDashboardId,
    })
  }, [dispatch, onWarning, savedDashboardQuery.data])

  useEffect(() => {
    if (savedDashboardQuery.isError) {
      dispatch({
        type: 'BOOTSTRAP_FAILED',
        error: 'Failed to load the saved dashboard copy.',
      })
    }
  }, [dispatch, savedDashboardQuery.isError])

  useEffect(() => {
    if (stateRef.current.dashboard || !stateRef.current.dashboardId) return
    if (!savedDashboardQuery.isSuccess || savedDashboardQuery.data !== null) return

    dispatch({
      type: 'BOOTSTRAP_FAILED',
      error: 'The saved dashboard could not be found.',
    })
  }, [dispatch, savedDashboardQuery.data, savedDashboardQuery.isSuccess])

  useEffect(() => {
    if (shouldWaitForSavedDashboardBootstrap) {
      dispatch({ type: 'BOOTSTRAP_STARTED' })
      return
    }

    if (!state.dashboard || !clientId || awaitingCatalogDefault || awaitingInitialView) {
      return
    }

    if (lastBootstrapLoadKeyRef.current === dashboardStructureKey && state.dashboardLoad.initialLoadCompleted) {
      return
    }

    lastBootstrapLoadKeyRef.current = dashboardStructureKey

    let cancelled = false
    void (async () => {
      await Promise.all([
        coordinator.loadSlicerOptions(),
        coordinator.refreshAllWidgets('initial-load'),
      ])

      if (!cancelled) {
        dispatch({ type: 'REFRESH_ALL_FINISHED' })
      }
    })()

    return () => {
      cancelled = true
    }
  }, [
    awaitingCatalogDefault,
    awaitingInitialView,
    clientId,
    coordinator,
    dashboardStructureKey,
    dispatch,
    shouldWaitForSavedDashboardBootstrap,
    state.dashboard,
    state.dashboardLoad.initialLoadCompleted,
  ])

  useEffect(() => {
    if (state.dashboard) {
      onPersistDashboard?.(state.dashboard, state.dashboardId)
    }
  }, [onPersistDashboard, state.dashboard, state.dashboardId])

  const replaceDashboard = useCallback((dashboard: DashboardDefinition, dashboardId: string | null) => {
    const changedWidgetIds = getChangedWidgetIds(stateRef.current.dashboard, dashboard, clientId)
    const changedWidgetIdSet = new Set(changedWidgetIds)
    const preserveWidgetIds = dashboard.widgets
      .filter((widget) => !changedWidgetIdSet.has(widget.id) && stateRef.current.widgets[widget.id])
      .map((widget) => widget.id)

    lastBootstrapLoadKeyRef.current = getDashboardStructureKey(dashboard, dashboardId)
    dispatch({
      type: 'DASHBOARD_REPLACED_SELECTIVE',
      dashboard,
      dashboardId,
      preserveWidgetIds,
    })
    // The replace resets the date range without presets. Re-resolve it against the catalog (its
    // default, or custom for an unlisted preset) before any request runs.
    const datePresetOptions = catalogDatePresetOptionsRef.current
    if (datePresetOptions) {
      dispatch({ type: 'DATE_DEFAULT_RESOLVED', datePresetOptions })
    }

    void coordinator.loadSlicerOptions()
    void Promise.all(changedWidgetIds.map(async (widgetId) => {
      const intent = createWidgetRequestIntent(stateRef.current, widgetId, 'refresh-all')
      if (intent) {
        await coordinator.runWidgetIntent(intent)
      }
    }))
  }, [clientId, coordinator, dispatch])

  const setDashboardId = useCallback((dashboardId: string | null) => {
    dispatch({
      type: 'SET_DASHBOARD_ID',
      dashboardId,
    })
  }, [dispatch])

  const changeDraftSlicer = useCallback((field: string, value: string | string[] | null) => {
    dispatch({ type: 'DRAFT_SLICER_CHANGED', field, value })
  }, [dispatch])

  const changeDraftDateRange = useCallback((dateRange: DashboardRuntimeDateRange) => {
    dispatch({ type: 'DRAFT_DATE_RANGE_CHANGED', dateRange })
  }, [dispatch])

  const resetDraftFilters = useCallback(() => {
    dispatch({ type: 'DRAFT_FILTERS_RESET' })
  }, [dispatch])

  const applyGlobalFilters = useCallback(async () => {
    if (!selectHasPendingGlobalFilterChanges(stateRef.current)) {
      return
    }

    dispatch({ type: 'APPLIED_FILTERS_COMMITTED' })
    await Promise.all([
      coordinator.loadSlicerOptions({ onlyChanged: true }),
      coordinator.refreshAllWidgets('global-filter-apply'),
    ])
    dispatch({ type: 'REFRESH_ALL_FINISHED' })
  }, [coordinator, dispatch])

  const refreshAll = useCallback(async () => {
    dispatch({ type: 'REFRESH_ALL_STARTED' })
    await Promise.all([
      coordinator.loadSlicerOptions(),
      coordinator.refreshAllWidgets('refresh-all'),
    ])
    dispatch({ type: 'REFRESH_ALL_FINISHED' })
  }, [coordinator, dispatch])

  const setWidgetPage = useCallback(async (widgetId: string, page: number) => {
    dispatch({ type: 'WIDGET_PAGE_SET', widgetId, page })

    const widget = stateRef.current.dashboard?.widgets.find((item) => item.id === widgetId)
    if (!widget || !hasWidgetHydrationPayload(widget)) {
      return
    }

    const session = stateRef.current.widgets[widgetId]
    const usesBackendPagination = session?.data.hasPaginationMetadata === true
      && session.data.totalRows > 0
      && session.data.pageSize > 0

    if (!usesBackendPagination) {
      return
    }

    const intent = createWidgetRequestIntent(stateRef.current, widgetId, 'page')
    if (intent) {
      await coordinator.runWidgetIntent(intent)
    }
  }, [coordinator, dispatch])

  const setWidgetDimension = useCallback(async (widgetId: string, groupByDimensions: string[] | null) => {
    const widget = stateRef.current.dashboard?.widgets.find((item) => item.id === widgetId)
    const normalizedGroupByDimensions = widget?.type === 'line'
      ? groupByDimensions?.slice(0, 1) ?? null
      : groupByDimensions

    dispatch({
      type: 'WIDGET_DIMENSION_SET',
      widgetId,
      groupByDimension: normalizedGroupByDimensions?.[0] ?? null,
      groupByDimensions: normalizedGroupByDimensions,
    })
    const intent = createWidgetRequestIntent(stateRef.current, widgetId, 'dimension')
    if (intent) {
      intent.query.groupByDimension = normalizedGroupByDimensions?.[0] ?? null
      intent.query.groupByDimensions = normalizedGroupByDimensions
      await coordinator.runWidgetIntent(intent)
    }
  }, [coordinator, dispatch])

  const setWidgetPickerState = useCallback((widgetId: string, pickerState: { field: string; value: string } | null) => {
    dispatch({ type: 'WIDGET_PICKER_SET', widgetId, pickerState })
  }, [dispatch])

  const setWidgetMeasureParameter = useCallback(async (widgetId: string, selections: Record<string, string> | null) => {
    dispatch({ type: 'WIDGET_MEASURE_PARAMETER_SET', widgetId, selections })
    const intent = createWidgetRequestIntent(stateRef.current, widgetId, 'measure-parameter')
    if (intent) {
      intent.query.measureParameterSelections = selections
      intent.query.page = 1
      await coordinator.runWidgetIntent(intent)
    }
  }, [coordinator, dispatch])

  const setWidgetDrilldown = useCallback(async (widgetId: string, drilldown: WidgetDrilldownState) => {
    dispatch({ type: 'WIDGET_DRILLDOWN_SET', widgetId, drilldown })
    const intent = createWidgetRequestIntent(stateRef.current, widgetId, 'drilldown')
    if (intent) {
      await coordinator.runWidgetIntent(intent)
    }
  }, [coordinator, dispatch])

  const clearWidgetDrilldown = useCallback(async (widgetId: string) => {
    dispatch({ type: 'WIDGET_DRILLDOWN_CLEARED', widgetId })
    const intent = createWidgetRequestIntent(stateRef.current, widgetId, 'clear-drilldown')
    if (intent) {
      await coordinator.runWidgetIntent(intent)
    }
  }, [coordinator, dispatch])

  const setWidgetSort = useCallback((widgetId: string, column: string | null, direction: 'asc' | 'desc') => {
    dispatch({
      type: 'WIDGET_PENDING_SORT_SET',
      widgetId,
      pendingSort: { column, direction },
    })

    const existing = sortTimeoutsRef.current[widgetId]
    if (existing) {
      clearTimeout(existing)
    }

    sortTimeoutsRef.current[widgetId] = setTimeout(() => {
      dispatch({
        type: 'WIDGET_SORT_SET',
        widgetId,
        column,
        direction,
      })
      const intent = createWidgetRequestIntent(stateRef.current, widgetId, 'sort')
      if (intent) {
        void coordinator.runWidgetIntent(intent)
      }
      sortTimeoutsRef.current[widgetId] = null
    }, 1000)
  }, [coordinator, dispatch])

  // Applies a saved view (or the dashboard defaults for null) and reloads every widget once.
  const applyView = useCallback(async (view: SavedDashboardViewState | null): Promise<ReconcileViewResult | null> => {
    const dashboard = stateRef.current.dashboard
    if (!dashboard) return null

    const result = reconcileViewState(view, dashboard, catalogDatePresetOptionsRef.current)
    if (result.unsupported) return result

    Object.values(sortTimeoutsRef.current).forEach((timer) => {
      if (timer) clearTimeout(timer)
    })
    sortTimeoutsRef.current = {}

    dispatch({ type: 'VIEW_APPLIED', ...result.state })
    await Promise.all([
      coordinator.loadSlicerOptions({ onlyChanged: true }),
      coordinator.refreshAllWidgets('view-apply'),
    ])
    dispatch({ type: 'REFRESH_ALL_FINISHED' })
    return result
  }, [coordinator, dispatch])

  const captureViewState = useCallback(() => serializeViewState(stateRef.current), [])

  return {
    state,
    dashboard: state.dashboard,
    dashboardId: state.dashboardId,
    draftFilters: selectDraftFilters(state),
    appliedFilters: selectAppliedFilters(state),
    hasPendingGlobalFilterChanges: selectHasPendingGlobalFilterChanges(state),
    slicerOptionsByField: selectSlicerOptionsByField(state),
    slicerLoadingByField: selectSlicerLoadingByField(state),
    hydratableWidgetIds: selectHydratableWidgetIds(state),
    insightSignature: selectInsightInvalidationSignature(state),
    widgetSessions: state.widgets,
    replaceDashboard,
    setDashboardId,
    actions: {
      changeDraftSlicer,
      changeDraftDateRange,
      resetDraftFilters,
      applyGlobalFilters,
      refreshAll,
      setWidgetPage,
      setWidgetSort,
      setWidgetDimension,
      setWidgetPickerState,
      setWidgetMeasureParameter,
      setWidgetDrilldown,
      clearWidgetDrilldown,
      applyView,
      captureViewState,
    },
  }
}
